import React, { useContext } from "react";
import { Card, CardContent } from "@/components/ui/card";
import {
  Edit,
  Trash2,
  Building2,
  CreditCard,
  Phone,
  Mail,
} from "lucide-react";
import { type Client } from "@/types";
import { Button } from "../ui/button";
import ConfirmActionModal from "../Modals/ConfirmActionModal";
import { LanguageProviderContext } from "@/providers/LanguageProvider";

interface ClientsCardProps {
  client: Client;
  onEdit: (client: Client) => void;
  onDelete: (id: string) => void;
  onShowInfo?: (client: Client) => void;
}

export default function ClientsCard({
  client,
  onEdit,
  onDelete,
  onShowInfo,
}: ClientsCardProps) {
  const { translator } = useContext(LanguageProviderContext);
  
  const handleCardClick = () => {
    if (onShowInfo) {
      onShowInfo(client);
    }
  };

  return (
    <Card
      onClick={handleCardClick}
      className="w-full hover:shadow-md transition-shadow cursor-pointer"
    >
      <CardContent className="py-3 px-4">
        <div className="flex items-center justify-end gap-4 w-full">
          <div className="flex min-w-0 flex-1 items-start justify-between gap-4 flex-wrap">
            {client.name && (
              <span className="flex items-center gap-2 text-sm text-muted-foreground truncate">
              <Building2 className="h-4 w-4 shrink-0 text-muted-foreground" />
                {client.name}
              </span>
            )}
            {client.identification && (
              <span className="flex items-center gap-2 text-sm text-muted-foreground truncate min-w-20">
                <CreditCard className="h-3.5 w-3.5 shrink-0" />
                {client.identification}
              </span>
            )}
            {client.phone?.number && (
              <span className="flex items-center gap-2 text-sm text-muted-foreground truncate min-w-20">
                <Phone className="h-3.5 w-3.5 shrink-0" />
                {client.phone.number}
              </span>
            )}
            {client.email && (
              <span className="flex items-center gap-2 text-sm text-muted-foreground truncate min-w-20">
                <Mail className="h-3.5 w-3.5 shrink-0" />
                {client.email}
              </span>
            )}
          </div>
          <div
            className="flex shrink-0 items-center gap-1"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              size="icon"
              variant="ghost"
              className="border border-primary"
              aria-label={translator("Clients.editClient")}
              onClick={() => onEdit(client)}
            >
              <Edit className="h-3 w-3" />
            </Button>
            <ConfirmActionModal
              title={translator("Clients.confirmDeleteClientTitle")}
              description={translator("Products.confirmDeleteDescription", {
                name: client.name,
              })}
              onConfirmAction={() => onDelete(client.id)}
              actionButtonVariant="destructive"
              buttonLabel={translator("Global.delete")}
              iconButton={<Trash2 className="h-3 w-3" />}
              size="icon"
              triggerClassName="border border-primary"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
